// Implements: F-UIX-018, F-RPT-009
/**
 * The exports of Mi consumo (owner 2026-10-19): the caller's consumption and cost per mockup, as
 * the server's export twins serve them (`/reports/me/{report}/export?format=csv|json`), self-only
 * like the page's reads. Mounted inside My usage's container, which the page keys by person
 * (checklist 14), so a format, a download under way or a refusal never follows to another person.
 *
 * A download is a plain `GET` through the shared client; its bytes are saved under the server's
 * file name, or the report's own name (`export_name`) when the answer names none. A refusal (an
 * export over the row cap) is shown with the server's sentence and nothing is saved. No storage.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { asApiError } from "../../../shared/api/asApiError";
import { useTranslation } from "../../../shared/i18n";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { saveBlob } from "../../../shared/ui/saveBlob";
import type { ExportFormat, MyUsageExport } from "../../../types/reports";
import { downloadMyUsageExport } from "../api/myUsageApi";
import { MyUsageExports } from "../components/MyUsageExports";

/** The server's file stem of each report (`export_name`), for an answer naming no file. */
const STEMS: Readonly<Record<MyUsageExport, string>> = { consumption: "mi-consumo", mockups: "mis-mockups" };

export function MyUsageExportsContainer(): ReactElement {
  const { t } = useTranslation();
  const [format, setFormat] = useState<ExportFormat>("csv");
  const [downloading, setDownloading] = useState<MyUsageExport | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const onDownload = async (report: MyUsageExport): Promise<void> => {
    setDownloading(report);
    setFailure(null);
    try {
      const file = await downloadMyUsageExport(report, format);
      saveBlob(file.blob, file.filename ?? `${STEMS[report]}.${format}`);
    } catch (cause) {
      setFailure(userMessage(asApiError(cause), t("backoffice.exports.failed")));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <MyUsageExports
      format={format}
      downloading={downloading}
      failure={failure}
      onFormat={setFormat}
      onDownload={(report) => {
        void onDownload(report);
      }}
    />
  );
}
